import { useState } from 'react'
import { AskBennyButton } from '@/components/assistant/AskBennyButton'
import { RecordButton } from '@/components/desktop/RecordButton'
import { PendingWritesIndicator } from '@/components/layout/PendingWritesIndicator'
import { TimeEntryWidget } from '@/components/time-entry'
import { useSidebar } from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import { useIsMobile } from '@/hooks/use-mobile'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { cn } from '@/lib/utils'
import { isTauri } from '@/lib/utils/tauri'
import { isWebRecordingSupported } from '@/lib/utils/web-recorder'

import { CustomSidebarTrigger } from './AppSidebar'
import { NotificationButton } from './NotificationButton'
import { OmniSearch } from './OmniSearch'
import { OrganizationSwitcher } from './OrganizationSwitcher'
import { UserMenu } from './UserMenu'

interface HeaderProps {
  onOpenChange?: (open: boolean) => void
}

export function Header({ onOpenChange }: HeaderProps) {
  const isMobile = useIsMobile()
  const { state } = useSidebar()
  const { isLoading: isLoadingFeatureFlags } = useFeatureFlags([
    'MEETING_NOTES',
  ])
  // RecordButton hides itself until MEETING_NOTES resolves, so on a cold boot
  // it used to pop in and nudge the header's centre. Hold its slot while the
  // flags are in flight — but only where recording could appear at all.
  const isRecordSlotPending = isLoadingFeatureFlags && isWebRecordingSupported()
  const isSidebarCollapsed = state === 'collapsed'
  // The collapsed search anchors itself right (ml-auto), so the row only
  // spans the header while the field is open; otherwise it hugs its content
  // and the Ask Benny pill sits directly left of the search button.
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)

  const isDesktopApp = isTauri() && !isMobile

  // For Tauri desktop app, the header is rendered at the MainLayout level
  // So we don't render anything here
  if (isDesktopApp) {
    return null
  }

  // Regular web/mobile header - white in light mode, black in dark mode
  return (
    <header
      className={cn(
        'sticky top-0 z-30 w-full transition-colors duration-300',
        'bg-white dark:bg-[hsl(var(--cast-iron-skillet))]',
        'border-b border-border/50',
      )}
    >
      <div
        className={cn(
          'flex w-full flex-wrap items-center gap-y-2 px-4 py-2 sm:h-14 sm:flex-nowrap sm:py-0',
        )}
      >
        {/* Left Side - Navigation & Time Entry */}
        <div className="order-1 flex flex-shrink-0 items-center gap-4">
          <div className="flex transition-transform duration-200 ease-out">
            <CustomSidebarTrigger className="flex" />
          </div>

          {/* Time Entry Widget + Meeting Recorder */}
          <div className="hidden items-center gap-2 sm:flex">
            <TimeEntryWidget />
            {isRecordSlotPending ? (
              <Skeleton className="h-8 w-[104px] rounded-md" />
            ) : (
              <RecordButton />
            )}
          </div>
        </div>

        {/* Center - OmniSearch + Benny */}
        <div className="order-3 flex w-full min-w-0 flex-1 items-center justify-center sm:order-2 sm:justify-end sm:px-2">
          <div
            className={cn(
              'flex items-center gap-2 sm:justify-end',
              isMobile || isSearchExpanded ? 'w-full sm:max-w-xl' : 'w-auto',
            )}
          >
            {/* The one Benny door on web: popover under the pill, Expand
                hands the thread to Conversations. Label folds on phones. */}
            <AskBennyButton labelClassName="max-sm:hidden" />
            {/* Desktop widths: collapses to a button on the right until used.
                Mobile keeps the full field on its own row. */}
            <OmniSearch
              className="w-full"
              collapsible={!isMobile}
              onExpandedChange={setIsSearchExpanded}
            />
          </div>
        </div>

        {/* Right Side - Organization & User */}
        <div className="order-2 flex flex-shrink-0 items-center gap-2 sm:order-3">
          {/* Utility Actions */}
          {/* Appearance lives in Settings → Account settings → Appearance. */}
          <div className="flex items-center gap-1">
            <NotificationButton />
          </div>
          {/* Renders only when a write is queued or the connection is down. */}
          <PendingWritesIndicator />
          {/* Organization Switcher */}
          <div
            className={cn(
              'hidden transition-opacity duration-200 sm:block',
              isSidebarCollapsed && !isMobile ? 'w-auto' : 'max-w-[200px]',
            )}
          >
            <OrganizationSwitcher className={isMobile ? 'w-full' : undefined} />
          </div>
          <div>
            <UserMenu
              onOpenChange={onOpenChange}
              getNavigationPath={path => path}
            />
          </div>
        </div>
      </div>
    </header>
  )
}
